import { Smartphone } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { WhatsAppConnectionFormState } from "./whatsapp-connection-config";

export function WhatsAppConnectionFields({
  value,
  onChange,
}: {
  value: WhatsAppConnectionFormState;
  onChange: (value: WhatsAppConnectionFormState) => void;
}) {
  const update = <K extends keyof WhatsAppConnectionFormState>(
    key: K,
    nextValue: WhatsAppConnectionFormState[K],
  ) => {
    onChange({ ...value, [key]: nextValue });
  };

  return (
    <div className="flex flex-col gap-4">
      <div
        className="rounded-[var(--radius-sm)] border px-3 py-3 text-xs flex gap-3"
        style={{
          borderColor: "var(--accent)",
          background: "var(--accent-subtle)",
          color: "var(--text-strong)",
        }}
      >
        <Smartphone size={16} className="mt-0.5 shrink-0" />
        <div className="flex flex-col gap-1">
          <span className="font-medium">WhatsApp linking is managed by Agentiq.</span>
          <span style={{ color: "var(--muted-foreground)" }}>
            Save the connection, apply the tenant snapshot, then scan the QR from this page.
          </span>
        </div>
      </div>

      {/* Delivery */}
      <Card>
        <CardHeader>
          <CardTitle>Delivery</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Default Target">
              <input
                value={value.defaultTo}
                onChange={(e) => update("defaultTo", e.target.value)}
                placeholder="+15550001111"
                className="form-input"
              />
              <FieldHint text="Default outbound target for CLI sends. E.164 number or group JID." />
            </FormField>

            <FormField label="Inbound Debounce (ms)">
              <input
                value={value.debounceMs}
                onChange={(e) => update("debounceMs", e.target.value)}
                placeholder="0"
                className="form-input"
              />
              <FieldHint text="Delay before processing rapid consecutive messages." />
            </FormField>

            <FormField label="Message Prefix">
              <input
                value={value.messagePrefix}
                onChange={(e) => update("messagePrefix", e.target.value)}
                placeholder="[Acme]"
                className="form-input"
              />
              <FieldHint text="Prepended to inbound messages." />
            </FormField>

            <FormField label="Response Prefix">
              <input
                value={value.responsePrefix}
                onChange={(e) => update("responsePrefix", e.target.value)}
                placeholder="Acme bot:"
                className="form-input"
              />
              <FieldHint text="Prepended to outbound responses." />
            </FormField>

            <FormField label="Media Max (MB)">
              <input
                value={value.mediaMaxMb}
                onChange={(e) => update("mediaMaxMb", e.target.value)}
                placeholder="20"
                className="form-input"
              />
              <FieldHint text="Maximum file size for media attachments." />
            </FormField>

            <FormField label="Chunk Mode">
              <select
                value={value.chunkMode}
                onChange={(e) => update("chunkMode", e.target.value)}
                className="form-input"
              >
                <option value="">Default</option>
                <option value="length">Length</option>
                <option value="newline">Newline</option>
              </select>
              <FieldHint text="How long messages are split into multiple WhatsApp messages." />
            </FormField>

            <FormField label="Chunk Limit (chars)">
              <input
                value={value.textChunkLimit}
                onChange={(e) => update("textChunkLimit", e.target.value)}
                placeholder="Default"
                className="form-input"
              />
              <FieldHint text="Max characters per message chunk." />
            </FormField>
          </div>
        </CardContent>
      </Card>

      {/* Behavior */}
      <Card>
        <CardHeader>
          <CardTitle>Behavior</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <CheckboxField
              label="Self chat mode"
              hint="Bot uses the same phone number as the sender"
              checked={value.selfChatMode}
              onCheckedChange={(checked) => update("selfChatMode", checked)}
            />
            <CheckboxField
              label="Send read receipts"
              hint="Mark messages as read when received"
              checked={value.sendReadReceipts}
              onCheckedChange={(checked) => update("sendReadReceipts", checked)}
            />
            <CheckboxField
              label="Block streaming"
              hint="Send complete responses instead of streaming"
              checked={value.blockStreaming}
              onCheckedChange={(checked) => update("blockStreaming", checked)}
            />
            <CheckboxField
              label="Allow config writes"
              hint="Let the channel write runtime config"
              checked={value.configWrites}
              onCheckedChange={(checked) => update("configWrites", checked)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function FieldHint({ text }: { text: string }) {
  return (
    <p className="text-[11px]" style={{ color: "var(--muted-foreground)" }}>
      {text}
    </p>
  );
}

function CheckboxField({
  label,
  hint,
  checked,
  onCheckedChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onCheckedChange(e.target.checked)}
        className="mt-0.5 shrink-0"
      />
      <div>
        <span className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>{label}</span>
        {hint && (
          <p className="text-[11px]" style={{ color: "var(--muted-foreground)" }}>{hint}</p>
        )}
      </div>
    </label>
  );
}
